import { formatSceneTime, impliedPrecision, markMoment, momentText, type MarkRole, type TimePrecision } from '@dispositio/core';
import { de } from '../i18n/de';

const REL = /^([+-])(\d+)(min|h|d|w|mo|y)(?:@(\d{2}):(\d{2}))?$/;
const YEARLESS = /^--(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/;
const DURATION = /^(\d+)(min|h|d|w|mo|y)$/;

/**
 * Bedeutung einer Zeitmarke in Worten (Tooltip im Text, Vorschau beim Setzen): „03.03.1823 07:00“, „ca. 03.1823“, „3 Tage danach“,
 * „am Tag danach, 07:00“, „am selben Tag, 19:00“, „3.3. (Jahr aus dem Zusammenhang)“; mit Rolle „Ende: …“, eine Dauer „Dauer: 3 Tage“.
 */
export function describeTimeMark(value: string, precision: TimePrecision | null, approx: boolean, role: MarkRole | null = null): string {
  const t = de.timeMarks;
  const d = DURATION.exec(value);
  if (d) {
    const [one, many] = t.units[d[2]!]!;
    return `${t.roles.duration}: ${approx ? 'ca. ' : ''}${d[1]} ${Number(d[1]) === 1 ? one : many}`;
  }
  const meaning = describeMoment(value, precision, approx);
  return role === 'start' || role === 'end' ? `${t.roles[role]}: ${meaning}` : meaning;
}

function describeMoment(value: string, precision: TimePrecision | null, approx: boolean): string {
  const t = de.timeMarks;
  const ca = approx ? 'ca. ' : '';
  const rel = REL.exec(value);
  if (rel) {
    const n = Number(rel[2]);
    const clock = rel[4] !== undefined ? `, ${rel[4]}:${rel[5]}` : '';
    if (rel[3] === 'd' && n === 0) return `${ca}${t.sameDay}${clock}`;
    if (rel[3] === 'd' && n === 1) return `${ca}${rel[1] === '+' ? t.nextDay : t.dayBefore}${clock}`;
    const [one, many] = t.units[rel[3]!]!;
    return `${ca}${n} ${n === 1 ? one : many} ${rel[1] === '+' ? t.after : t.before}${clock}`;
  }
  const y = YEARLESS.exec(value);
  if (y) return `${ca}${Number(y[2])}.${Number(y[1])}.${y[3] !== undefined ? ` ${y[3]}:${y[4]}` : ''} ${t.yearFromContext}`;
  const m = markMoment(value, null);
  return m ? formatSceneTime(momentText(m), { precision: precision ?? impliedPrecision(value), approx }) : value;
}
